/**
 * OpenAPI API Client (hand-authored wrapper over generated types)
 *
 * This module is HAND-AUTHORED: it wires openapi-fetch with the auth/identity
 * interceptor, the network error-recovery layer (timeout/offline/retry), and the
 * classified error copy. Only the TYPE definitions it consumes
 * (`@/types/api.generated`) are auto-generated from the backend's OpenAPI spec.
 * To regenerate those types: ./scripts/sync-api-types.sh (from repo root; canonical
 * entrypoint — do NOT run `npm run generate:api` directly, it drops the
 * spec-hash header and pre-commit will reject the commit)
 *
 * Usage:
 *   import { api } from '@/api/generated-client'
 *
 *   // All paths are type-safe and autocompleted
 *   const { data, error } = await api.GET('/auth/me')
 *   const { data, error } = await api.POST('/auth/login', {
 *     body: { email: 'user@example.com', password: '***' }
 *   })
 */

import createClient from 'openapi-fetch'
import type { paths } from '@/types/api.generated'
import { classifyNetworkError, type NetworkErrorState } from '@/api/network-error'
import { useNetworkStatus } from '@/composables/useNetworkStatus'
import { useAuthStore } from '@/stores/auth'
import { useUIStore } from '@/stores/ui'

// Base URL for API requests
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'
const REQUEST_TIMEOUT_MS = 15000
const UNAUTHORIZED_COPY = classifyNetworkError({
  response: { status: 401 } as Response,
})

// Create the typed client
function safeReportNetworkError(): ((state: NetworkErrorState) => void) | null {
  try {
    return useNetworkStatus().reportError
  } catch {
    return null
  }
}

async function fetchWithRecovery(input: RequestInfo, init: RequestInit = {}): Promise<Response> {
  const controller = new AbortController()
  const timedOut = setTimeout(() => {
    controller.abort()
  }, REQUEST_TIMEOUT_MS)

  const safeReport = safeReportNetworkError()

  try {
    const response = await globalThis.fetch(input, {
      ...init,
      signal: controller.signal,
    })
    return response
  } catch (thrown) {
    const offline = typeof navigator === 'undefined' ? false : !navigator.onLine
    const state = classifyNetworkError({
      thrown,
      timedOut: thrown instanceof DOMException && thrown.name === 'AbortError',
      offline,
    })

    if (state.retryable) {
      safeReport?.(state)
    }

    throw new Error(state.message)
  } finally {
    clearTimeout(timedOut)
  }
}

const client = createClient<paths>({
  baseUrl: API_BASE_URL,
  fetch: fetchWithRecovery,
})

// Guard: `useAuthStore()` / `useUIStore()` throw ("getActivePinia() was
// called with no active Pinia") when invoked outside a context with an
// active Pinia instance (e.g. certain background/microtask timing edge
// cases). A network-layer interceptor must never crash the whole request
// pipeline over this — fall through to `null` so callers can degrade to the
// forced-relogin / no-op path instead of throwing.
function safeStore<T>(getStore: () => T): T | null {
  try {
    return getStore()
  } catch {
    return null
  }
}

// STAB-P1-01 S2 fix: a 401 retry must never read the ORIGINAL request's body
// stream — by the time `onResponse` runs, that stream has already been
// consumed/locked by the fetch that produced the 401 (re-using a
// locked/disturbed stream in `new Request()` throws). Stash a clone taken
// BEFORE the request is sent (in `onRequest`, when the body is still
// unconsumed) and use that as the retry source instead.
const pristineRequestClones = new WeakMap<Request, Request>()

// Add auth header middleware
client.use({
  async onRequest({ request }) {
    // Get token from auth store (Pinia)
    const authStore = safeStore(useAuthStore)
    if (authStore?.accessToken) {
      request.headers.set('Authorization', `Bearer ${authStore.accessToken}`)
    }
    // Multi-role view switcher: tell the backend which identity the user is
    // currently acting as. The backend treats this as a DOWNGRADE-ONLY hint
    // (validated against the JWT roles; can only lower privilege, never raise),
    // so e.g. a site_owner viewing as "customer" gets customer-scoped data.
    // See ADR-014. Only sent when it differs from the JWT primary role.
    const activeIdentity = authStore?.currentIdentity
    if (authStore && activeIdentity && activeIdentity !== authStore.user?.role) {
      request.headers.set('X-Active-Identity', activeIdentity)
    }
    // Stash a pristine clone before this request is dispatched, so a later
    // 401 retry (see retryWithFreshToken) can rebuild the request from an
    // unconsumed body stream.
    pristineRequestClones.set(request, request.clone())
    return request
  },
  async onResponse({ request, response }) {
    // STAB-P1-01 S2: refresh-once 401 interceptor.
    //
    // 行為：
    //   1. 401 從「非 auth 端點」(排除 /auth/refresh, /auth/login, /auth/logout,
    //      /public/, /line/) 收到 → 試一次 refresh via auth store。
    //   2. refresh 成功 → 用新 access token 重發原 request 一次,並回傳。
    //   3. refresh 失敗 (revoked / expired / reused / rate-limited) → 清掉
    //      auth state + toast 「登入已過期,請重新登入」+ 跳 /login。
    //
    // 防護：
    //   - **refresh storm**: 並發 401 共用同一個 in-flight refresh Promise
    //     (auth store 內部 refreshInFlight 處理)。
    //   - **infinite loop**: /auth/refresh 本身的 401 不再嘗試 refresh,
    //     直接走 forced-relogin。
    //   - **preserved**: X-Active-Identity / X-Tenant header 由 caller
    //     注入;retry 時由 openapi-fetch 重新跑 onRequest,自動保留。
    //   - **no double-toast**: 401 已自處理的 case (retried 或 forced-relogin)
    //     不再走下面通用的 error notify,避免兩則通知並存。
    const url = new URL(request.url)
    const path = url.pathname
    const isPublicEndpoint =
      path.includes('/public/') || path.includes('/line/')
    // /auth/refresh, /auth/login, /auth/logout 都不走 refresh-once：
    //   - /auth/refresh 失敗 = forced re-login（不可遞迴 refresh 自己）
    //   - /auth/login 失敗 = 密碼錯,本來就要 surface error
    //   - /auth/logout 失敗 = idempotent,忽略即可
    const isAuthEndpoint =
      path.endsWith('/auth/refresh') ||
      path.endsWith('/auth/login') ||
      path.endsWith('/auth/logout')

    let authHandled = false

    if (response.status === 401 && !isPublicEndpoint && !isAuthEndpoint) {
      const authStore = safeStore(useAuthStore)
      if (authStore?.refreshToken) {
        try {
          await authStore.refresh()
          // 重新發原 request 一次。用 onRequest 階段就存好的 pristine clone 重建,
          // 換 Authorization 後 fetch(見 retryWithFreshToken)。
          const retried = await retryWithFreshToken(request, authStore.accessToken!)
          authHandled = true
          // 回傳 retried response 給 caller;caller 再讀 data/error 時已是
          // 新 token 取得的結果。
          // 注意:retried 若是 2xx,下面 !response.ok 不觸發;若仍是非 2xx
          // (e.g. retry 後 403),仍會顯示 error — 那是正確語意。
          return retried
        } catch {
          // refresh 失敗:auth store 內部已 clearAuthData,這裡做 forced-relogin。
          authHandled = true
          const uiStore = safeStore(useUIStore)
          uiStore?.notify({
            type: 'warning',
            title: UNAUTHORIZED_COPY.title,
            message: UNAUTHORIZED_COPY.message,
            duration: 0, // 不自動消失,使用者一定要處理
          })
          if (window.location.pathname !== '/login') {
            window.location.href = '/login'
          }
          // 仍 return 原 401 response 給 caller（讓 component error state 仍能更新）
          return response
        }
      }
      // 沒 refresh token(或無 active Pinia,見 safeStore)→ 直接走 forced-relogin。
      authHandled = true
      authStore?.clearAuthData()
      const uiStore = safeStore(useUIStore)
      uiStore?.notify({
        type: 'warning',
        title: UNAUTHORIZED_COPY.title,
        message: UNAUTHORIZED_COPY.message,
        duration: 0,
      })
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }

    // Handle errors - show notification
    // 401 handled 過的 path 不再 notify(避免「登入已過期」+「HTTP 401」兩則並存)。
    if (!authHandled && !response.ok) {
      let state = classifyNetworkError({ response })
      const uiStore = safeStore(useUIStore)

      try {
        const body = await response.clone().json()
        state = classifyNetworkError({
          response,
          serverMessage: typeof body?.message === 'string' ? body.message : undefined,
        })
      } catch {
        state = classifyNetworkError({ response })
      }

      if (state.retryable) {
        const safeReport = safeReportNetworkError()
        safeReport?.(state)
        return response
      }

      if (uiStore) {
        uiStore.notify({
          type: 'error',
          title: state.title,
          message: state.message,
        })
      }
    }

    return response
  },
})

// Retry helper: rebuild the request from the PRISTINE clone captured in
// onRequest (before the original was ever dispatched), swap in the fresh
// access token, and re-fetch. Using `originalRequest` itself here would read
// an already-consumed/locked body stream (the fetch that produced the 401
// already transmitted it) — `new Request()` throws on a locked/disturbed
// stream, and `Request.clone()` itself throws once the body is unusable, so
// the ONLY safe source is a clone taken pre-dispatch (see
// `pristineRequestClones` above).
async function retryWithFreshToken(
  originalRequest: Request,
  freshAccessToken: string,
): Promise<Response> {
  const source = pristineRequestClones.get(originalRequest) ?? originalRequest
  const headers = new Headers(source.headers)
  headers.set('Authorization', `Bearer ${freshAccessToken}`)
  try {
    const retryRequest = new Request(source.url, {
      method: source.method,
      headers,
      body: source.body,
      // credentials / mode / referrer 等沿用預設(openapi-fetch 用同源策略)。
      credentials: source.credentials,
      mode: source.mode,
      referrer: source.referrer,
      referrerPolicy: source.referrerPolicy,
    })
    return await fetch(retryRequest)
  } catch {
    // Guard: body stream unusable (locked/disturbed) even on the pristine
    // clone — degrade to a bodyless retry rather than throwing and breaking
    // the whole 401 interceptor (an uncaught throw here would surface as an
    // unhandled rejection instead of the intended forced-relogin fallback).
    const retryRequest = new Request(source.url, {
      method: source.method,
      headers,
      credentials: source.credentials,
      mode: source.mode,
      referrer: source.referrer,
      referrerPolicy: source.referrerPolicy,
    })
    return fetch(retryRequest)
  }
}

// Export the typed client
export const api = client

// Re-export types for convenience
export type { paths }
export type { components, operations } from '@/types/api.generated'
